import React from "react";
import { Box, Dialog, IconButton, Typography, keyframes } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import tonOnlyImg from "../../img/ton618/ton-only.jpg";
import holeImg from "../../img/ton618/hole.jpg";

const MotionBox = motion(Box);

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.08 + i * 0.07,
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.45; }
  50% { opacity: 0.9; }
`;

function SunOrb({ size = 56 }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "50%",
        background: `
          radial-gradient(circle at 32% 30%, #fff8c4 0%, #ffe566 14%, #ffb300 42%, #ff8a00 72%, #c44a00 100%)
        `,
        boxShadow: `
          0 0 ${Math.round(size * 0.45)}px rgba(255, 170, 40, 0.55),
          inset -${Math.round(size * 0.12)}px -${Math.round(size * 0.14)}px ${Math.round(size * 0.28)}px rgba(140, 40, 0, 0.4)
        `,
        position: "relative",
        "&::before, &::after": {
          content: '""',
          position: "absolute",
          borderRadius: "50%",
          background: "rgba(60, 20, 0, 0.45)",
        },
        "&::before": {
          width: "14%",
          height: "12%",
          top: "38%",
          left: "42%",
        },
        "&::after": {
          width: "9%",
          height: "8%",
          top: "52%",
          left: "58%",
        },
      }}
    />
  );
}

function StephensonOrb({ size = 68 }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "50%",
        background: `
          radial-gradient(circle at 34% 28%, #ffb090 0%, #ff6a3a 22%, #e53935 38%, #c62828 55%, #6a1010 100%)
        `,
        boxShadow: `
          0 0 ${Math.round(size * 0.4)}px rgba(255, 80, 40, 0.5),
          inset -${Math.round(size * 0.1)}px -${Math.round(size * 0.12)}px ${Math.round(size * 0.3)}px rgba(40, 0, 0, 0.45)
        `,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background: `
            radial-gradient(ellipse 40% 30% at 60% 55%, rgba(255,200,120,0.35) 0%, transparent 70%),
            radial-gradient(ellipse 25% 20% at 30% 65%, rgba(80,10,10,0.5) 0%, transparent 70%)
          `,
        },
      }}
    />
  );
}

/** Círculo TON: foto limpia, agujero centrado en el orbe. */
function TonOrb({ size = 76 }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "50%",
        overflow: "hidden",
        position: "relative",
        background: "#000",
        boxShadow: `0 0 ${Math.round(size * 0.42)}px rgba(255, 140, 40, 0.55)`,
        border: "1px solid rgba(255, 180, 80, 0.22)",
      }}
    >
      <Box
        component="img"
        src={holeImg}
        alt=""
        sx={{
          position: "absolute",
          // Zoom al centro del agujero; un pelín a la izquierda para quitar UI
          width: "210%",
          height: "210%",
          left: "50%",
          top: "50%",
          transform: "translate(-52%, -50%)",
          objectFit: "cover",
          objectPosition: "center center",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 50% 50%, transparent 40%, rgba(0,0,0,0.2) 72%, rgba(0,0,0,0.55) 100%)",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
}

function TinyDot({ color, size = 7 }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        flexShrink: 0,
        background: color,
        boxShadow: `0 0 ${Math.max(6, size)}px ${color}`,
      }}
    />
  );
}

const SCALE_ROWS = [
  {
    key: "sol",
    labelKey: "Login.ton.sun",
    Orb: SunOrb,
    orbSize: 56,
    rightKey: "Login.ton.earth",
    rightDot: { color: "#5b9fd4", size: 7 },
    i: 4,
  },
  {
    key: "steph",
    labelKey: "Login.ton.stephenson",
    Orb: StephensonOrb,
    orbSize: 68,
    rightKey: "Login.ton.sun",
    rightDot: { color: "#ffb300", size: 9 },
    i: 5,
  },
  {
    key: "ton",
    labelKey: "Login.ton.ton618",
    Orb: TonOrb,
    orbSize: 76,
    rightKey: "Login.ton.stephensonShort",
    rightDot: { color: "#e07070", size: 8 },
    i: 6,
  },
];

/**
 * Dialog sin scroll: dato Ton-618 + escala con orbes claros y tipografía propia.
 */
export default function Ton618InfoDialog({ open, onClose }) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      TransitionProps={{ timeout: 360 }}
      BackdropProps={{
        sx: { bgcolor: alpha("#000", 0.78), backdropFilter: "blur(6px)" },
      }}
      PaperProps={{
        sx: {
          m: { xs: 1.5, sm: 3 },
          width: "min(1040px, calc(100vw - 24px))",
          height: "min(560px, calc(100vh - 24px))",
          maxHeight: "calc(100vh - 24px)",
          bgcolor: "#000",
          color: "#fff",
          overflow: "hidden",
          borderRadius: { xs: 2.5, sm: 3.5 },
          border: `1px solid ${alpha("#ffb300", 0.22)}`,
          boxShadow: `0 32px 80px ${alpha("#000", 0.75)}, 0 0 60px ${alpha("#ff8a1a", 0.12)}`,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <IconButton
        aria-label={t("Login.ton.close")}
        onClick={onClose}
        sx={{
          position: "absolute",
          top: { xs: 8, sm: 12 },
          right: { xs: 8, sm: 12 },
          zIndex: 3,
          color: "#fff",
          bgcolor: alpha("#000", 0.45),
          border: `1px solid ${alpha("#fff", 0.16)}`,
          backdropFilter: "blur(10px)",
          "&:hover": { bgcolor: alpha("#fff", 0.12) },
        }}
      >
        <CloseRoundedIcon fontSize="small" />
      </IconButton>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.08fr 1fr" },
          gridTemplateRows: { xs: "48% 52%", md: "1fr" },
          overflow: "hidden",
        }}
      >
        {/* Izquierda: foto + letras */}
        <Box
          sx={{
            position: "relative",
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            px: { xs: 2.5, sm: 3.5 },
            pb: { xs: 2.5, sm: 3.25 },
            pt: 5,
            borderRight: {
              xs: "none",
              md: `1px solid ${alpha("#fff", 0.08)}`,
            },
            borderBottom: {
              xs: `1px solid ${alpha("#fff", 0.08)}`,
              md: "none",
            },
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              overflow: "hidden",
              bgcolor: "#000",
            }}
          >
            {/* Foto recortada: solo el agujero, sin texto de la captura */}
            <Box
              component="img"
              src={tonOnlyImg}
              alt=""
              sx={{
                position: "absolute",
                width: "108%",
                height: "62%",
                left: "50%",
                top: "2%",
                transform: "translateX(-50%)",
                objectFit: "cover",
                objectPosition: "55% 45%",
                filter: "saturate(1.12) contrast(1.04)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: `
                  linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.12) 40%, #000 58%, #000 100%),
                  radial-gradient(ellipse 65% 45% at 55% 28%, rgba(255,140,40,0.14) 0%, transparent 60%)
                `,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: "6%",
                left: "55%",
                width: { xs: "58%", md: "52%" },
                aspectRatio: "1",
                transform: "translate(-50%, 0)",
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(255,180,60,0.22) 0%, transparent 68%)",
                animation: `${pulseGlow} 4.5s ease-in-out infinite`,
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                },
              }}
            />
          </Box>

          <MotionBox
            initial="hidden"
            animate={open ? "visible" : "hidden"}
            sx={{ position: "relative", zIndex: 1, maxWidth: 420 }}
          >
            <MotionBox variants={fadeUp} custom={0}>
              <Typography
                sx={{
                  fontSize: { xs: "0.62rem", sm: "0.68rem" },
                  fontWeight: 700,
                  letterSpacing: "0.26em",
                  textTransform: "uppercase",
                  color: alpha("#fff", 0.65),
                  mb: 1,
                }}
              >
                {t("Login.ton.why")}
              </Typography>
            </MotionBox>
            <MotionBox variants={fadeUp} custom={1}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.1rem", sm: "1.35rem" },
                  lineHeight: 1.2,
                  color: "#fff",
                }}
              >
                {t("Login.ton.approx")}
              </Typography>
            </MotionBox>
            <MotionBox variants={fadeUp} custom={2}>
              <Typography
                sx={{
                  mt: 0.35,
                  fontWeight: 900,
                  fontSize: { xs: "1.35rem", sm: "1.85rem" },
                  lineHeight: 1.12,
                  letterSpacing: "-0.03em",
                  background:
                    "linear-gradient(105deg, #ffe566 0%, #ffb300 45%, #ff8a1a 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                {t("Login.ton.suns")}
              </Typography>
            </MotionBox>
            <MotionBox variants={fadeUp} custom={3}>
              <Typography
                sx={{
                  mt: 0.45,
                  fontWeight: 700,
                  fontSize: { xs: "0.95rem", sm: "1.15rem" },
                  lineHeight: 1.3,
                  color: alpha("#fff", 0.92),
                }}
              >
                {t("Login.ton.fit")}
                <br />
                {t("Login.ton.hole")}
              </Typography>
            </MotionBox>
          </MotionBox>
        </Box>

        {/* Derecha: escala */}
        <Box
          sx={{
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            px: { xs: 2.5, sm: 3.5 },
            py: { xs: 2, sm: 2.75 },
            gap: 1.25,
          }}
        >
          <Typography
            sx={{
              flexShrink: 0,
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: alpha("#ffb300", 0.9),
            }}
          >
            {t("Login.ton.scale")}
          </Typography>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-evenly",
              py: 0.5,
            }}
          >
            {SCALE_ROWS.map((row) => {
              const Orb = row.Orb;
              return (
                <MotionBox
                  key={row.key}
                  custom={row.i}
                  variants={fadeUp}
                  initial="hidden"
                  animate={open ? "visible" : "hidden"}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto",
                    alignItems: "center",
                    columnGap: { xs: 1.25, sm: 2 },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 84, sm: 100 },
                      height: { xs: 84, sm: 100 },
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                      transform: { xs: "scale(0.9)", sm: "scale(1)" },
                    }}
                  >
                    <Orb size={row.orbSize} />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "0.88rem", sm: "1.05rem" },
                      color: "#fff",
                      minWidth: 0,
                    }}
                  >
                    {t(row.labelKey)}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.9,
                      justifyContent: "flex-end",
                    }}
                  >
                    <TinyDot
                      color={row.rightDot.color}
                      size={row.rightDot.size}
                    />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.72rem", sm: "0.82rem" },
                        color: alpha("#fff", 0.75),
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t(row.rightKey)}
                    </Typography>
                  </Box>
                </MotionBox>
              );
            })}
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              textAlign: { xs: "left", md: "center" },
              pt: 0.25,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "0.78rem", sm: "0.88rem" },
                fontWeight: 600,
                lineHeight: 1.35,
                color: alpha("#fff", 0.72),
                mb: 0.75,
                whiteSpace: "pre-line",
              }}
            >
              {t("Login.ton.punch")}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "0.72rem", sm: "0.8rem" },
                fontWeight: 800,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#ffb300",
              }}
            >
              {t("Login.ton.because")}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
